type QueryKeyBase = readonly unknown[];

export interface QueryKeyConfig {
  entity: string;
  operation?: "list" | "detail";
  filters?: Record<string, unknown>;
  params?: Record<string, unknown>;
  id?: string | number;
}

/**
 * Generates consistent query keys for React Query hooks
 *
 * @param config - Configuration object for generating query keys
 * @returns Array of query key segments
 *
 * @example
 * // Simple entity query
 * generateQueryKey({ entity: 'tickets' })
 * // Returns: ['tickets']
 *
 * @example
 * // Entity with operation
 * generateQueryKey({ entity: 'tickets', operation: 'list' })
 * // Returns: ['tickets', 'list']
 *
 * @example
 * // Entity with ID
 * generateQueryKey({ entity: 'tickets', id: 123 })
 * // Returns: ['tickets', 123]
 *
 * @example
 * // Complex query with filters and params
 * generateQueryKey({
 *   entity: 'tickets',
 *   operation: 'list',
 *   filters: { status: 'open', priority: 'high' },
 *   params: { page: 1, limit: 50 }
 * })
 * // Returns: ['tickets', 'list', { status: 'open', priority: 'high' }, { page: 1, limit: 50 }]
 */
export function generateQueryKey(config: QueryKeyConfig): QueryKeyBase {
  const { entity, operation, filters, params, id } = config;

  const keySegments: unknown[] = [entity];

  if (operation) {
    keySegments.push(operation);
  }

  if (id !== undefined) {
    keySegments.push(id);
  }

  if (filters && Object.keys(filters).length > 0) {
    keySegments.push(filters);
  }

  if (params && Object.keys(params).length > 0) {
    keySegments.push(params);
  }

  return keySegments;
}

/**
 * Helper functions for common query key patterns
 */
export const queryKeys = {
  // Tickets
  tickets: {
    all: () => generateQueryKey({ entity: "tickets" }),
    list: (
      filters?: Record<string, unknown>,
      params?: Record<string, unknown>
    ) =>
      generateQueryKey({
        entity: "tickets",
        operation: "list",
        filters,
        params,
      }),
    detail: (id: string | number) =>
      generateQueryKey({ entity: "tickets", id }),
    // qa: (id: string | number) =>
    //   generateQueryKey({ entity: "tickets", id, operation: "qa" }),
  },

  // Projects
  projects: {
    all: () => generateQueryKey({ entity: "projects" }),
    list: (
      filters?: Record<string, unknown>,
      params?: Record<string, unknown>
    ) =>
      generateQueryKey({
        entity: "projects",
        operation: "list",
        filters,
        params,
      }),
    detail: (id: string | number) =>
      generateQueryKey({ entity: "projects", id }),
    // milestones: (id: string | number) =>
    //   generateQueryKey({ entity: "projects", id, operation: "milestones" }),
    // tasks: (id: string | number) =>
    //   generateQueryKey({ entity: "projects", id, operation: "tasks" }),
  },

  // Customers
  customers: {
    all: () => generateQueryKey({ entity: "customers" }),
    list: (
      filters?: Record<string, unknown>,
      params?: Record<string, unknown>
    ) =>
      generateQueryKey({
        entity: "customers",
        operation: "list",
        filters,
        params,
      }),
    detail: (id: string | number) =>
      generateQueryKey({ entity: "customers", id }),
    // users: (id: string | number) =>
    //   generateQueryKey({ entity: "customers", id, operation: "users" }),
    // sites: (id: string | number) =>
    //   generateQueryKey({ entity: "customers", id, operation: "sites" }),
  },

  // Users
  users: {
    all: () => generateQueryKey({ entity: "users" }),
    list: (
      filters?: Record<string, unknown>,
      params?: Record<string, unknown>
    ) =>
      generateQueryKey({ entity: "users", operation: "list", filters, params }),
    detail: (id: string | number) => generateQueryKey({ entity: "users", id }),
    // profile: () => generateQueryKey({ entity: "users", operation: "profile" }),
  },

  // Configurations
  configurations: {
    // boards: () =>
    //   generateQueryKey({ entity: "configurations", operation: "boards" }),
    // tags: () =>
    //   generateQueryKey({ entity: "configurations", operation: "tags" }),
    // sla: () => generateQueryKey({ entity: "configurations", operation: "sla" }),
    // attributes: (type?: string) =>
    //   generateQueryKey({
    //     entity: "configurations",
    //     operation: "attributes",
    //     params: type ? { type } : undefined,
    //   }),
    // templates: {
    //   ticket: () =>
    //     generateQueryKey({
    //       entity: "configurations",
    //       operation: "templates-ticket",
    //     }),
    //   project: () =>
    //     generateQueryKey({
    //       entity: "configurations",
    //       operation: "templates-project",
    //     }),
    //   email: () =>
    //     generateQueryKey({
    //       entity: "configurations",
    //       operation: "templates-email",
    //     }),
    // },
  },

  // Dashboard & Reports
  dashboard: {
    // overview: () =>
    //   generateQueryKey({ entity: "dashboard", operation: "overview" }),
    // metrics: (filters?: Record<string, unknown>) =>
    //   generateQueryKey({ entity: "dashboard", operation: "metrics", filters }),
  },

  reports: {
    list: (type?: string, filters?: Record<string, unknown>) =>
      generateQueryKey({
        entity: "reports",
        operation: "list",
        filters,
        params: type ? { type } : undefined,
      }),
  },

  // Recurring Tickets (Control Center → Service Desk entity)
  recurringTickets: {
    all: () => generateQueryKey({ entity: "recurring_tickets" }),
    list: (
      filters?: Record<string, unknown>,
      params?: Record<string, unknown>
    ) =>
      generateQueryKey({
        entity: "recurring_tickets",
        operation: "list",
        filters,
        params,
      }),
    detail: (id: string | number) =>
      generateQueryKey({ entity: "recurring_tickets", id }),
    auditTrail: (id: string | number) =>
      ["recurring_tickets", "audit_trail", id] as const,
  },

  // Audit trail (ticket / project / task timeline drawer)
  auditTrail: {
    byService: (serviceType: string, serviceId: string) =>
      ["audit_trail", serviceType, serviceId] as const,
  },

  // Public API Admin Keys (Control Center → public API access)
  apiKeys: {
    all: () => generateQueryKey({ entity: "api_keys" }),
    list: (
      filters?: Record<string, unknown>,
      params?: Record<string, unknown>
    ) =>
      generateQueryKey({
        entity: "api_keys",
        operation: "list",
        filters,
        params,
      }),
    detail: (id: string | number) =>
      generateQueryKey({ entity: "api_keys", id }),
  },

  // Integrations
  integrations: {
    list: () => generateQueryKey({ entity: "integrations" }),
    detail: (provider: string) =>
      generateQueryKey({ entity: "integrations", id: provider }),
    // status: (provider: string) =>
    //   generateQueryKey({
    //     entity: "integrations",
    //     id: provider,
    //     operation: "status",
    //   }),
  },

  // Channel Settings (IT-Connect Config)
  itConnectConfig: {
    all: () => generateQueryKey({ entity: "it-connect-config" }),
  },

  // Pinned Messages
  pinnedMessages: {
    all: () => generateQueryKey({ entity: "pinned-messages" }),
    list: (serviceId: string) =>
      generateQueryKey({ entity: "pinned-messages", operation: "list", id: serviceId }),
  },

  // Chat Search
  chatSearch: {
    list: (serviceId: string, query: string, threadId?: string) =>
      generateQueryKey({
        entity: "chat-search",
        operation: "list",
        id: serviceId,
        filters: { q: query, ...(threadId && { thread_id: threadId }) },
      }),
  },

  // Agent Configuration (Control Center → AI Agents)
  agentConfig: {
    all: () => generateQueryKey({ entity: "agent_config" }),
    tones: () => generateQueryKey({ entity: "agent_config", operation: "list", id: "tones" }),
    list: (params?: Record<string, unknown>) =>
      generateQueryKey({
        entity: "agent_config",
        operation: "list",
        params,
      }),
    detail: (id: string) =>
      generateQueryKey({ entity: "agent_config", id }),
    trainingFiles: (id: string) =>
      ["agent_config", "training_files", id] as const,
    // NOTE: entity is "agent_tickets", so agentConfig.all() invalidation does NOT cover these keys (read-only endpoints; deliberate).
    tickets: (params?: Record<string, unknown>) =>
      generateQueryKey({
        entity: "agent_tickets",
        operation: "list",
        params,
      }),
    ticketReasoning: (ticketId: string, agentId: string) =>
      ["agent_tickets", "reasoning", ticketId, agentId] as const,
    ticketActivity: (ticketId: string, mspId: string) =>
      ["agent_tickets", "activity", ticketId, mspId] as const,
    guidance: (agentId: string) => ["agent_guidance", agentId] as const,
  },
};
